import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { IntersectionEntities } from '@/hooks/useIntersectionListener';
import { getSelectedSection } from '@/hooks/useIntersectionListener';
import { useTracking } from 'react-tracking';
import useUser from '@/hooks/useUser';
import { useFlag } from '@/features/unleash';
import { intersectorThresholds } from '@/constants/intersectorThresholds';
import { useScrollDirection } from 'use-scroll-direction';

interface ContextValue {
  observe?: (instance: HTMLElement | null) => void;
}

const DwellTrackingContext = createContext<ContextValue | undefined>(undefined);

/**
 * This hook will have no effect outside DwellTrackingProvider
 * To add a target element to be observed, set these props on your element like this:
 *
 * const { observe } = useDwellTracking();
 *
 * const { section } = useReportSection();
 *```
 * <TargetElement
 *     ref={observe}
 *     data-cy-id={'id'}
 *     data-section={section}
 * >
 *```
 */
export function useDwellTracking() {
  const context = useContext(DwellTrackingContext);
  if (context === undefined) {
    return {};
  }
  return context;
}

interface Props {
  children: React.ReactElement;
}
const DwellTrackingProvider = (props: Props) => {
  const flag = useFlag({ name: 'mono-exit-tracking-events' });
  const enabled =
    flag.flagsReady &&
    flag.enabled &&
    flag.variant.name === 'has-dwell-tracking';
  const { trackEvent } = useTracking();
  const { data: user } = useUser();
  const [observer, setObserver] = useState<IntersectionObserver>();
  const [selected, setSelected] = useState<string>();
  const intersectionEntities = useRef<IntersectionEntities>({});
  const previousSelectedId = useRef<string>();
  const previousEntryTimeRef = useRef<number>(Date.now());
  const { isScrollingDown } = useScrollDirection({ timeToReset: 750 });

  //Get threshold value from unleash flag
  const durationThreshold = useMemo(() => {
    let threshold = 0;
    if (flag.variant.payload?.type === 'json') {
      try {
        //Value is stored in seconds in the "threshold" field
        const parsed = JSON.parse(flag.variant.payload?.value);
        if (parsed?.threshold) {
          threshold = parsed?.threshold * 1000;
        }
      } catch (error) {}
    }
    return threshold;
  }, [flag]);

  //Adds ref of element to track
  const observe = useCallback(
    (instance: HTMLElement | null) => {
      if (!instance || !observer || !enabled) return;
      observer.observe(instance);
    },
    [observer, enabled]
  );

  const trackExitEvent = useCallback(() => {
    if (!previousSelectedId.current) return;
    const now = Date.now();
    const duration = now - previousEntryTimeRef.current;
    if (duration < durationThreshold) return;
    const [section, target] = previousSelectedId.current.split('|');

    trackEvent({
      type: 'track',
      action: 'viewed',
      subject: section,
      modifier: target,
      duration,
      timestamp: now,
      authenticated: user.loggedIn,
    });
  }, [trackEvent, user.loggedIn, durationThreshold]);

  useEffect(() => {
    if (!enabled) return;
    if (RUNTIME_ENV === 'server' || typeof document === 'undefined') return;

    const callback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const attributes = entry.target.attributes;
        const dataCyId = attributes['data-cy-id']?.value;
        const section = attributes['data-section']?.value;
        const id = dataCyId?.replace('report-sub-section-', '');
        if (!id || !section) return;
        //Track targets in the form of section.id
        const key = `${section}|${id}`;
        //1. Update entities
        if (!entry.isIntersecting) {
          delete intersectionEntities.current[key];
        } else {
          const {
            isIntersecting,
            intersectionRatio,
            boundingClientRect: { top },
            intersectionRect: { height },
          } = entry;
          const body = document.body;
          const documentElement = document.documentElement;
          let scrollTop = 0;
          try {
            scrollTop =
              window.pageYOffset || documentElement.scrollTop || body.scrollTop;
          } catch {
            scrollTop = 0;
          }
          const clientTop = documentElement.clientTop || body.clientTop || 0;

          intersectionEntities.current[key] = {
            top,
            coverage: height / window.innerHeight,
            isIntersecting,
            intersectionRatio,
            yPosToDocument: top + scrollTop - clientTop,
          };
        }
        //2. Get entity with best coverage
        const selected = getSelectedSection(
          Object.keys(intersectionEntities.current),
          intersectionEntities.current,
          !isScrollingDown
        );
        setSelected(selected);
      });
    };
    const newObserver = new IntersectionObserver(callback, {
      threshold: intersectorThresholds,
    });
    setObserver(newObserver);
    trackEvent({
      type: 'track',
      action: 'started',
      subject: 'dwell_tracking',
    });

    return () => {
      newObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Avoid experiment deps adding additional dwell_tracking
  }, [enabled, trackEvent]);

  //Track tab & exit events
  useEffect(() => {
    const blurHandler = () => {
      trackExitEvent();
    };
    const focusHandler = () => {
      previousEntryTimeRef.current = Date.now();
    };
    const beforeUnloadHandler = () => {
      trackExitEvent();
    };

    window.addEventListener('blur', blurHandler);
    window.addEventListener('focus', focusHandler);
    window.addEventListener('beforeunload', beforeUnloadHandler);

    return () => {
      window.removeEventListener('blur', blurHandler);
      window.removeEventListener('focus', focusHandler);
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [trackExitEvent]);

  //Listen to changes in selected sub section
  useEffect(() => {
    if (!selected) return;
    trackExitEvent();
    previousEntryTimeRef.current = Date.now();
    previousSelectedId.current = selected;
  }, [selected, trackExitEvent]);

  const value = useMemo(() => ({ observe }), [observe]);

  return (
    <DwellTrackingContext.Provider value={value}>
      {props.children}
    </DwellTrackingContext.Provider>
  );
};
export default DwellTrackingProvider;
